import React, { useState } from 'react';
import { Layout as AntLayout, Menu, Button, Dropdown, Avatar, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import useHasPermission, { checkCanViewFinanceModule } from '../../hooks/useHasPermission';
import NotificationBell from '../common/NotificationBell';
import OperationalRealtimeSync from '../../realtime/OperationalRealtimeSync';
import GlobalSearch from '../common/GlobalSearch';
import { buildMenuConfig } from '../../config/menuConfig';
import { formatUserDisplayName } from '../../utils/userDisplayName';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  // Считаем мобильным экран до 768px
  const isInitialMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  const [collapsed, setCollapsed] = useState(isInitialMobile);
  const [isMobile, setIsMobile] = useState(isInitialMobile);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, canManageUsers } = useAuth();
  const showAdmin = canManageUsers();

  // Меню навигации. Скрываем пункты, если у пользователя нет соответствующих прав
  const canViewFinance = checkCanViewFinanceModule(user);
  const canViewReferences = useHasPermission('references.view');
  const canViewReports = useHasPermission('reports.view');
  const canViewPOA = useHasPermission('poa.view');
  const canViewDocuments = useHasPermission('documents.view');
  const canViewAnalytics = useHasPermission('analytics.view');

  const menuItems = buildMenuConfig({
    canViewFinance,
    canViewDocuments,
    canViewPOA,
    canViewAnalytics,
    canViewReferences,
    canViewReports,
    showAdmin,
  });

  const handleMenuClick = ({ key }: { key: string }) => {
    // Игнорируем клики по родительским элементам (подразделам)
    if (key === 'tools' || key === 'documents' || key === 'finance') {
      return;
    }
    navigate(key);
  };

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Меню профиля пользователя
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Профиль',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Настройки',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      onClick: handleLogout,
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'settings') {
      navigate('/settings');
    } else if (key === 'profile') {
      navigate('/profile');
    }
  };

  const pathFirst = location.pathname.split('/')[1];
  const selectedKey = pathFirst ? (pathFirst === 'control' || pathFirst === 'finance' ? location.pathname : `/${pathFirst}`) : '/dashboard';
  
  // Определяем открытые подразделы на основе текущего пути
  React.useEffect(() => {
    const path = location.pathname;
    const newOpenKeys: string[] = [];
    
    if (path.startsWith('/analytics/') || path.startsWith('/calculations') || path.startsWith('/visual-packing') || path.startsWith('/tools/') || path.startsWith('/control/')) {
      newOpenKeys.push('tools');
    }
    
    if (path.startsWith('/tn') || path.startsWith('/partner-applications') || path.startsWith('/poa')) {
      newOpenKeys.push('documents');
    }
    
    if (path.startsWith('/finance')) {
      newOpenKeys.push('finance');
    }
    
    setOpenKeys(newOpenKeys);
  }, [location.pathname]);

  // Отслеживаем изменение ширины окна, чтобы переключать мобильный / десктопный режим
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // На мобильном всегда держим сайдбар свёрнутым, чтобы не съедать экран
      if (mobile) {
        setCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}>
      <OperationalRealtimeSync />
      <AntLayout className="min-h-screen" style={{ minHeight: '100vh' }}>
        {/* Сайдбар */}
        <Sider 
          trigger={null} 
          collapsible 
          collapsed={collapsed}
          className="shadow-lg"
          theme="light"
          width={isMobile ? 64 : 256}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100
          }}
        >
          {/* Логотип */}
          <div className="flex items-center justify-center py-4 px-6 border-b border-gray-200">
            {!collapsed ? (
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">
                  Логистика v2.0
                </div>
                <div className="text-xs text-gray-500">
                  Система управления
                </div>
              </div>
            ) : (
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">Л</span>
              </div>
            )}
          </div>

          {/* Навигационное меню */}
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            items={menuItems}
            onClick={handleMenuClick}
            className="border-none"
            style={{ paddingTop: '8px' }}
          />
        </Sider>

        <AntLayout
          style={{
            // На мобильном фиксированный небольшой отступ под узкий сайдбар,
            // на десктопе — как раньше, в зависимости от collapsed
            marginLeft: isMobile ? 64 : collapsed ? 80 : 256,
            transition: 'margin-left 0.2s',
          }}
        >
          {/* Хедер */}
          <Header className="px-4 flex items-center justify-between" style={{ backgroundColor: 'var(--bg)', color: 'var(--text)', borderBottom: `1px solid var(--card-border)`, position: 'sticky', top: 0, zIndex: 99 }}>
            {/* Левая часть - кнопка сворачивания */}
            <div className="flex items-center">
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                className="mr-4 border-none"
                style={{ backgroundColor: 'transparent', color: 'var(--text)' }}
              />
              <Text className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {new Date().toLocaleDateString('ru-RU', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Text>
            </div>

            {/* Правая часть - поиск, уведомления и профиль */}
            <div className="flex items-center space-x-4">
              {/* Глобальный поиск — на мобильном прячем, чтобы не загромождать хедер */}
              {!isMobile && <GlobalSearch />}
              
              {/* Уведомления */}
              <div className="rounded-lg" style={{ backgroundColor: 'var(--card)', color: 'var(--text)' }}>
                <NotificationBell />
              </div>

              {/* Профиль пользователя */}
              <Dropdown
                menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                placement="bottomRight"
                trigger={['click']}
              >
                <div className="flex items-center space-x-2 cursor-pointer px-3 py-2 rounded-lg" style={{ backgroundColor: 'transparent' }}>
                  <Avatar 
                    size="small" 
                    icon={<UserOutlined />} 
                    className="bg-blue-500"
                  />
                  <div className="text-sm">
                    <div className="font-medium" style={{ color: 'var(--text)' }}>
                      {user ? formatUserDisplayName(user) : 'Пользователь'}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {user?.role?.name || 'Роль не определена'}
                    </div>
                  </div>
                </div>
              </Dropdown>
            </div>
          </Header>

          {/* Основной контент */}
          <Content style={{ backgroundColor: 'var(--bg)' }}>
            {children}
          </Content>
        </AntLayout>
      </AntLayout>
    </div>
  );
};

export default Layout; 